<template>
    <div class="send-http-request-process-modal">
        <ProcessModal
            :is-open="isOpen"
            show-save
            :size="modalSize"
            data-qa="send-http-request-process-modal-process"
            :title="$t('funnel.edit.http.request.title')"
            :summary-text="$t('funnel.edit.http.request.summary.description')"
            :process-tip-text="$t('funnel.edit.send.http.request.tip.overview')"
            :process-tip-link="$t('funnel.edit.http.request.tip.url')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #additionalHeaderContent>
                <DsDropdown
                    ref="dropdown"
                >
                    <template #default>
                        <DsIconButton name="more-vertical" class="more-button" />
                    </template>

                    <template #menu>
                        <DsMultiselectMenu
                            v-model="menuCategory"
                            categorized
                            :options="menuCategoryOptions"
                            class="menu-category-multiselect"
                            @selectCategory="setSelectCategory"
                            @input="handleInput"
                        />
                    </template>
                </DsDropdown>
            </template>
            <template #processParameters>
                <div class="vertical-container">
                    <div class="wrap-request-url-input">
                        <DsMultiselect
                            v-model="httpRequestType"
                            label-prop="label"
                            value-prop="value"
                            required
                            bind-value-only
                            :submitted="submitted"
                            class="request-http-method"
                            :options="httpMethodOptions"
                            :placeholder="$t('funnel.edit.http.request.method')"
                        >
                            <template #help>
                                <span v-if="submitted && !httpRequestType" data-qa="invalid-requestType">
                                    {{ $t('funnel.edit.http.request.field.message') }}
                                </span>
                            </template>
                        </DsMultiselect>
                        <MergeableInput
                            v-model="httpUrl"
                            data-qa="request-url-input-field"
                            required
                            :label="$t('funnel.edit.http.request.url.placeholder')"
                            class="request-url-input-field"
                            :submitted="submitted"
                            :error-text="$t('funnel.edit.http.validation.url')"
                            :invalid="!isValidRequestUrl"
                            :merge-field-categories="mergeFields"
                        />
                    </div>
                    <span class="query-parameters">
                        {{ $t('funnel.edit.http.request.query.parameters') }}
                    </span>
                    <div class="query-params-container">
                        <div
                            v-for="(queryParam) in queryParams"
                            :key="queryParam.id"
                            class="new-name-value-pair-param"
                        >
                            <div class="query-wrap-inputs">
                                <DsInputField
                                    v-model="queryParam.name"
                                    :label="$t('funnel.edit.send.http.request.name.placeholder')"
                                    class="new-name-input-field"
                                />
                                <MergeableInput
                                    v-model="queryParam.value"
                                    :label="$t('funnel.edit.send.http.request.value.placeholder')"
                                    class="new-value-input-field"
                                    :merge-field-categories="mergeFields"
                                    :maxlength="512"
                                />
                            </div>
                            <span @click="removeQueryParams(queryParam.id)">
                                <DsIcon name="trash-2" class="circle-button-remove" />
                            </span>
                        </div>
                        <div
                            v-if="areQueryParamsSet"
                            class="new-name-value-pair-param"
                        >
                            <div class="query-wrap-inputs">
                                <DsInputField
                                    v-model="queryName"
                                    :label="$t('funnel.edit.send.http.request.name.placeholder')"
                                    class="new-name-input-field"
                                    @blur="onBlurSaveQueryParam"
                                />
                                <MergeableInput
                                    v-model="queryValue"
                                    :label="$t('funnel.edit.send.http.request.value.placeholder')"
                                    class="new-value-input-field"
                                    :merge-field-categories="mergeFields"
                                    :maxlength="512"
                                    @blur-input="onBlurSaveQueryParam"
                                />
                            </div>
                            <span @click="removeEmptyQueryParams">
                                <DsIcon name="trash-2" class="circle-button-remove" />
                            </span>
                        </div>
                        <span class="add-query-span" @click="showAddQueryParamBox">
                            <DsIcon name="add" class="button-add-query-param" />
                            <span class="add-name-value-pair">
                                {{ $t('funnel.edit.http.request.add.query.parameters') }}
                            </span>
                        </span>
                    </div>
                    <span class="header-parameters">
                        {{ $t('funnel.edit.http.request.header.parameters') }}
                    </span>
                    <div class="header-param-container">
                        <div
                            v-for="(headerParam) in headerParams"
                            :key="headerParam.id"
                            class="new-name-value-pair-param"
                        >
                            <div class="header-wrap-inputs">
                                <DsInputField
                                    v-model="headerParam.name"
                                    :label="$t('funnel.edit.send.http.request.name.placeholder')"
                                    class="new-name-input-field"
                                />
                                <MergeableInput
                                    v-model="headerParam.value"
                                    :label="$t('funnel.edit.send.http.request.value.placeholder')"
                                    class="new-value-input-field"
                                    :merge-field-categories="mergeFields"
                                    :maxlength="512"
                                />
                            </div>
                            <span @click="removeHeaderParams(headerParam.id)">
                                <DsIcon name="trash-2" class="circle-button-remove" />
                            </span>
                        </div>
                        <div
                            v-if="areHeaderParamsSet"
                            class="new-name-value-pair-param"
                        >
                            <div class="header-wrap-inputs">
                                <DsInputField
                                    v-model="headerName"
                                    :label="$t('funnel.edit.send.http.request.name.placeholder')"
                                    class="new-name-input-field"
                                    @blur="onBlurSaveHeaderParam"
                                />
                                <MergeableInput
                                    v-model="headerValue"
                                    :label="$t('funnel.edit.send.http.request.value.placeholder')"
                                    class="new-value-input-field"
                                    :merge-field-categories="mergeFields"
                                    :maxlength="512"
                                    @blur-input="onBlurSaveHeaderParam"
                                />
                            </div>
                            <span @click="removeEmptyHeaderParams">
                                <DsIcon name="trash-2" class="circle-button-remove" />
                            </span>
                        </div>
                        <span class="add-query-span" @click="showAddHeaderParamBox">
                            <DsIcon name="add" class="button-add-header-param" />
                            <span class="add-name-value-pair">
                                {{ $t('funnel.edit.http.request.add.header.parameters') }}
                            </span>
                        </span>
                    </div>
                    <span class="body-request">
                        {{ $t('funnel.edit.http.request.body.parameters') }}
                    </span>
                    <div class="body-request-div">
                        <CampaignBuilderMergeableTextArea
                            v-model="requestBody"
                            :funnel-id="funnelId"
                            class="body-request-text-area"
                            :merge-field-categories="mergeFields"
                            :has-legacy-format="true"
                            :maxlength="$options.BODY_MAX_CHARACTER_COUNT + 1"
                            :has-error="isMessageTooLong"
                            :class="[{ 'error': isMessageTooLong }]"
                        >
                            <template #error>
                                <div v-if="isMessageTooLong" class="character-count">
                                    <span class="character-count-error">{{ $t('funnel.edit.http.request.body.message.limit', {maxLength : $options.BODY_MAX_CHARACTER_COUNT }) }}</span>
                                </div>
                            </template>
                        </CampaignBuilderMergeableTextArea>
                    </div>
                    <div class="http-testing-wrapper">
                        <div class="http-request-testing">
                            <span class="testing-request">
                                {{ $t('funnel.edit.http.request.request') }}
                            </span>
                            <DsTextAreaField
                                v-model="testRequest"
                                class="test-request-text-area"
                                type="text"
                            />
                        </div>
                        <div class="http-response-testing">
                            <span class="testing-response">
                                {{ $t('funnel.edit.http.request.response') }}
                            </span>
                            <DsTextAreaField
                                v-model="testResponse"
                                class="test-response-text-area"
                                type="text"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </ProcessModal>
        <HttpRequestCopyFromCampaign
            v-if="showCopyFromCampaignListModal"
            :is-open="showCopyFromCampaignListModal"
            @close-copy-from-campaign-list="closeCopyFromCampaignListModal"
            @http-request-copy-from-campaign="fetchHttpRequestFromCampaign"
        />
    </div>
</template>
<script>
import MergeableInput from '@/components/shared/MergeableInput.vue';
import ProcessModal from '../processModal/ProcessModal.vue';
import { getMergeFieldsForCB } from '../../../../configuration/task.flow.api';
import HttpRequestCopyFromCampaign from '../httpRequestCopyFromCampaign/HttpRequestCopyFromCampaign.vue';
import CampaignBuilderMergeableTextArea from '../../../whens/campaignBuilderMergeableTextArea/CampaignBuilderMergeableTextArea.vue';
import {
    testHttpRequest,
    getAuthorsMenus,
    createHttpRequest,
    saveCopyFromCampaign,
    getHttpRequestConfig,
    saveHttpRequestConfig,
    deleteRequestParameter,
    getHttpRequestCallType,
    saveOrUpdateHttpRequestParameter,
} from './send.http.request.api';

const URL_PATTERN = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,16}\.?)(\/(~?[\w.-]+~?\/?)*)*\/?$/i;

export const TEMP_ID = 'tempId';
export const PARAMETER_TYPE = {
    query: 'QUERY',
    header: 'HEADER',
};

export const BODY_MAX_CHARACTER_COUNT = 5000;

export default {
    TEMP_ID,
    URL_PATTERN,
    PARAMETER_TYPE,
    BODY_MAX_CHARACTER_COUNT,
    components: {
        ProcessModal,
        MergeableInput,
        HttpRequestCopyFromCampaign,
        CampaignBuilderMergeableTextArea,
    },
    props: {
        funnelId: {
            type: Number,
            default: 0,
        },
        isOpen: {
            type: Boolean,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    httpRequestConfigId: null,
                },
            }),
        },
    },
    emits: ['close-modal', 'update-cell-value'],
    data() {
        return {
            modalSize: 'lg',
            httpRequestType: null,
            requestBody: '',
            menuCategory: null,
            selectedCategory: null,
            httpUrl: null,
            queryName: null,
            queryValue: null,
            headerName: null,
            headerValue: null,
            testRequest: null,
            testResponse: null,
            httpRequestConfigId: null,
            httpMethodOptions: [],
            menuCategoryOptions: [],
            queryParams: [],
            headerParams: [],
            mergeFields: [],
            isValidRequestUrl: true,
            submitted: false,
            showCopyFromCampaignListModal: false,
        };
    },

    computed: {
        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },
        areQueryParamsSet() {
            return this.queryName !== null && this.queryValue !== null;
        },
        areHeaderParamsSet() {
            return this.headerName !== null && this.headerValue !== null;
        },
        isMessageTooLong() {
            return this.requestBody.length > BODY_MAX_CHARACTER_COUNT;
        },
    },

    async mounted() {
        await this.createCategories();
        await this.getHttpRequestTypes();
        await this.fetchHttpRequestFunnel();
        this.mergeFields = await getMergeFieldsForCB(this.funnelId);
    },

    methods: {
        async handleInput(data) {
            switch (this.selectedCategory) {
            case 0:
                await this.sendTest(data.value);
                break;
            case 1:
                this.showCopyFromCampaignListModal = true;
                break;
            default:
                break;
            }
        },
        async createCategories() {
            const dataMenus = await getAuthorsMenus();

            const { userMap } = dataMenus;

            const users = Object.entries(userMap).map(([value, label]) => {
                return { value, label };
            });

            this.menuCategoryOptions = [
                {
                    category: { value: 'actions', label: this.$t('funnel.edit.send.http.request.menu.item.user.test'), icon: 'user' },
                    options: users,
                },
                {
                    category: { value: 'actions', label: this.$t('funnel.edit.send.http.request.menu.item.copy.campaign'), icon: 'copy' },
                },
            ];
        },
        async sendTest(userId) {
            try {
                await this.checkNewParams(this.queryParams, PARAMETER_TYPE.query);
                await this.checkNewParams(this.headerParams, PARAMETER_TYPE.header);

                const payload = {
                    httpRequestType: this.httpRequestType,
                    httpRequestBody: this.requestBody !== null ? this.requestBody : '',
                    httpRequestTargetUrl: this.httpUrl,
                    httpRequestQueryParams: this.convertParamsToArray(this.queryParams),
                    httpRequestHeaderParams: this.convertParamsToArray(this.headerParams),
                    httpRequestConfigId: this.httpRequestConfigId,
                    contactId: userId,
                };

                const { request, response } = await testHttpRequest(payload);

                this.testRequest = request;
                this.testResponse = response;

                const successMessage = this.$t('funnel.edit.send.http.request.test.success.message');

                this.$toast({
                    message: successMessage,
                });
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.send.http.request.test.error.message');

                this.$error({
                    message: errorMessage,
                });
            }
        },
        async getHttpRequestTypes() {
            try {
                this.httpMethodOptions = await getHttpRequestCallType();
            } catch (error) {
                this.$error({ message: this.$t('funnel.edit.http.request.method.load.error') });
            }
        },
        async fetchHttpRequestFromCampaign(id) {
            const { httpRequestConfig, httpRequestParametersQUERY, httpRequestParametersHEADER } = await getHttpRequestConfig(id);

            this.httpRequestType = httpRequestConfig.callType;
            this.httpUrl = httpRequestConfig.targetUrl;
            this.requestBody = httpRequestConfig.requestBody;

            if (httpRequestParametersQUERY !== null) {
                this.queryParams = this.convertParamsToMap(httpRequestParametersQUERY);
            }

            if (httpRequestParametersHEADER !== null) {
                this.headerParams = this.convertParamsToMap(httpRequestParametersHEADER);
            }

            this.testRequest = '';
            this.testResponse = '';

            try {
                await saveCopyFromCampaign(this.httpRequestConfigId, id);
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.send.http.post.copy.campaign.failed.message');

                this.$error({
                    message: errorMessage,
                });
            }
        },
        async fetchHttpRequestFunnel() {
            let tempRequestConfigId = null;

            if (this.cell?.value?.httpRequestConfigId) {
                tempRequestConfigId = this.cell.value.httpRequestConfigId;
            } else {
                tempRequestConfigId = await createHttpRequest();
            }

            const cell = { ...this.cell };

            cell.value = cell.value || {};

            cell.value.httpRequestConfigId = tempRequestConfigId;


            const { httpRequestConfig, httpRequestParametersQUERY, httpRequestParametersHEADER } = await getHttpRequestConfig(tempRequestConfigId);

            this.httpRequestConfigId = tempRequestConfigId.toString().replace('L', '');
            this.httpRequestType = httpRequestConfig.callType || 'POST';
            this.httpUrl = httpRequestConfig.targetUrl || '';
            this.requestBody = httpRequestConfig.requestBody || '';

            if (httpRequestParametersQUERY !== null) {
                this.queryParams = this.convertParamsToMap(httpRequestParametersQUERY);
            }

            if (httpRequestParametersHEADER !== null) {
                this.headerParams = this.convertParamsToMap(httpRequestParametersHEADER);
            }
        },
        async fetchHttpRequestParams(parameterType) {
            const { httpRequestParametersQUERY, httpRequestParametersHEADER } = await getHttpRequestConfig(this.httpRequestConfigId);

            if (httpRequestParametersQUERY !== null && parameterType === PARAMETER_TYPE.query) {
                this.queryParams = this.convertParamsToMap(httpRequestParametersQUERY);
            }

            if (httpRequestParametersHEADER !== null && parameterType === PARAMETER_TYPE.header) {
                this.headerParams = this.convertParamsToMap(httpRequestParametersHEADER);
            }
        },
        async updateConfig() {
            this.submitted = true;
            this.setValidRequestUrl();

            if (!this.isValidRequestUrl) {
                return;
            }

            const cell = { ...this.cell };

            cell.value = cell.value || {};

            cell.value.httpRequestConfigId = this.httpRequestConfigId;

            await this.checkNewParams(this.queryParams, PARAMETER_TYPE.query);
            await this.checkNewParams(this.headerParams, PARAMETER_TYPE.header);

            const payload = {
                httpRequestType: this.httpRequestType,
                httpRequestBody: this.requestBody !== null ? this.requestBody : '',
                httpRequestTargetUrl: this.httpUrl,
                httpRequestQueryParams: this.convertParamsToArray(this.queryParams),
                httpRequestHeaderParams: this.convertParamsToArray(this.headerParams),
                httpRequestConfigId: this.httpRequestConfigId,
            };

            try {
                await saveHttpRequestConfig(this.httpRequestConfigId, payload);

                this.$emit('update-cell-value', { cell });

                this.submitted = false;
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.send.http.request.save.configuration');

                this.$error({
                    message: errorMessage,
                });
            }
        },
        async checkNewParams(params, parameterType) {
            if (params != null) {
                const tempParameter = params.find((item) => item.id.toString().includes(`${this.$options.TEMP_ID}_`));

                if (tempParameter) {
                    const payload = {
                        httpRequestConfigId: this.httpRequestConfigId,
                        httpRequestType: parameterType,
                        parameterArray: this.convertParamsToArray(params),
                    };

                    await this.saveOrUpdateParameter(payload);
                    await this.fetchHttpRequestParams(parameterType);
                }
            }
        },
        async saveOrUpdateParameter(payload) {
            try {
                await saveOrUpdateHttpRequestParameter(this.httpRequestConfigId, payload);
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.send.http.request.save.configuration');

                this.$error({
                    message: errorMessage,
                });
            }
        },
        async removeQueryParams(id) {
            if (id !== null) {
                if (!this.hasTempId(id)) {
                    await deleteRequestParameter(id);
                }
                this.queryParams = this.queryParams.filter((param) => {
                    return param.id !== id;
                });
            } else {
                this.removeEmptyQueryParams();
            }
        },
        async removeHeaderParams(id) {
            if (id !== null) {
                if (!this.hasTempId(id)) {
                    await deleteRequestParameter(id);
                }
                this.headerParams = this.headerParams.filter((param) => {
                    return param.id !== id;
                });
            } else {
                await this.removeEmptyHeaderParams();
            }
        },
        setSelectCategory(data) {
            this.selectedCategory = data.index;
        },
        setValidRequestUrl() {
            this.isValidRequestUrl = this.httpUrl?.match(this.$options.URL_PATTERN) !== null;
        },
        hasTempId(id) {
            return id.toString().includes(`${this.$options.TEMP_ID}_`);
        },
        onBlurSaveQueryParam () {
            if (this.queryName !== '' && this.queryValue !== '') {
                const newParamObj = {
                    id: `${this.$options.TEMP_ID}_${this.queryParams.length + 1}`,
                    name: this.queryName,
                    value: this.queryValue,
                };

                this.queryParams.push(newParamObj);
                this.queryName = null;
                this.queryValue = null;
            }
        },
        onBlurSaveHeaderParam () {
            if (this.headerName !== '' && this.headerValue !== '') {
                const newParamObj = {
                    id: `${this.$options.TEMP_ID}_${this.headerParams.length + 1}`,
                    name: this.headerName,
                    value: this.headerValue,
                };

                this.headerParams.push(newParamObj);
                this.headerName = null;
                this.headerValue = null;
            }
        },
        removeEmptyQueryParams() {
            this.queryName = null;
            this.queryValue = null;
        },
        removeEmptyHeaderParams() {
            this.headerName = null;
            this.headerValue = null;
        },
        showAddQueryParamBox() {
            if (this.queryName === null) {
                this.queryName = '';
            }

            if (this.queryValue === null) {
                this.queryValue = '';
            }
        },
        showAddHeaderParamBox() {
            if (this.headerName === null) {
                this.headerName = '';
            }

            if (this.headerValue === null) {
                this.headerValue = '';
            }
        },
        convertParamsToMap(parameters) {
            const paramsMap = parameters.map((obj) => ({
                id: obj.id,
                name: obj.name,
                value: obj.value,
            }));

            return paramsMap;
        },
        convertParamsToArray(mapParams) {
            const paramsArray = [];

            mapParams.forEach((param) => {
                const arrayValue = [
                    param.id,
                    param.name,
                    param.value,
                ];

                paramsArray.push(arrayValue);
            });

            return paramsArray;
        },
        closeCopyFromCampaignListModal() {
            this.showCopyFromCampaignListModal = false;
        },
        closeModal() {
            this.$emit('close-modal');
        },
    },
};
</script>
<style lang="scss">
.vertical-container {
    display: flex;
    flex-direction: column;
}

.wrap-request-url-input {
    display: flex;
    flex-direction: row;
}

.query-wrap-inputs,
.header-wrap-inputs {
    display: flex;
    flex-direction: row;
    width: 96%;
}

.request-http-method {
    max-width: 20%;
    margin-right: $spacing-200;
}
.menu-category-multiselect {
    white-space: nowrap;
}
.add-name-value-pair,
.button-add-query-param,
.button-add-header-param {
    color: $color-blue-700;
}

.mergeable-input.request-url-input-field {
    margin-bottom: 2rem;
    width: 72%;
}
div.wrap-request-url-input > div.mergeable-input.request-url-input-field > div > input {
    height: 2.6rem;
}
.input-field.new-name-input-field {
    margin-bottom: 0;
}
.query-parameters,
.header-parameters,
.body-request,
.testing-request,
.testing-response {
    font-weight: bold;
    margin-left: $spacing-100;
    padding-top: $spacing-200;
    margin-bottom: $spacing-250;
}

.header-parameters,
.body-request {
    border-top: solid px-to-rem(1px) $color-gray-200;
}

.add-query-span {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    cursor: pointer;
    height: $spacing-400;
    margin-bottom: $spacing-250;
}

.body-request-text-area {
    --textarea-min-height: #{px-to-rem(150px)};
}

.http-testing-wrapper {
    display: flex;
    padding-top: $spacing-200;
    justify-content: space-between;
    border-top: solid px-to-rem(1px) $color-gray-200;
}

.http-request-testing,
.http-response-testing {
    width: 49%;
}

.send-http-request-process-modal .input-field.test-request-text-area,
.send-http-request-process-modal .input-field.test-response-text-area {
    margin-top: $spacing-100;
    --textarea-min-height: #{px-to-rem(150px)};
}

.character-count-error {
    color: $color-red;
    display: inline-block;
    max-width: 60%;
    font-size: $font-size-sm;
}

.body-request-text-area.no-label.error > textarea {
    border-color: #e02500;
}

</style>
